import { 
  collection, 
  doc, 
  addDoc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  setDoc,
  query, 
  where, 
  orderBy,
  Timestamp,
  increment,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';

export interface Referral {
  id?: string;
  referrerUserId: string;
  referredUserId: string;
  referralCode: string;
  status: 'pending' | 'completed' | 'cancelled';
  creditAmount: number; // Amount credited to referrer (in USD)
  referredUserEmail?: string;
  createdAt: Timestamp;
  completedAt?: Timestamp;
}

export interface ReferralClick {
  id?: string;
  referralCode: string;
  referrerUserId: string;
  clickedAt: Timestamp;
  userAgent?: string;
  ipAddress?: string;
}

const REFERRAL_CREDIT_AMOUNT = 15; // $15 per successful referral
const REFERRED_DISCOUNT_AMOUNT = 15; // $15 discount for referred user

/**
 * Generate a unique referral code for a user
 * Format: First 3 letters of email + random 4 alphanumeric characters
 */
export const generateReferralCode = (email: string): string => {
  const emailPrefix = email.split('@')[0].toUpperCase().slice(0, 3);
  const randomChars = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `${emailPrefix}${randomChars}`;
};

/**
 * Build a stable referral code from email + user id without querying other users' documents.
 * (Collection queries on `users` for uniqueness often fail under rules that only allow reads of `users/{ownUid}`.)
 */
const buildReferralCodeForUser = (userId: string, email: string): string => {
  const emailPrefix = (email.split('@')[0] || 'USR')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 3)
    .padEnd(3, 'X');
  const idTail = userId.replace(/-/g, '').toUpperCase().slice(-8);
  return `${emailPrefix}${idTail}`;
};

/**
 * Get or create referral code for a user
 */
export const getOrCreateReferralCode = async (userId: string, email: string): Promise<string> => {
  const userRef = doc(db, 'users', userId);
  const userSnap = await getDoc(userRef);

  if (userSnap.exists()) {
    const userData = userSnap.data();
    if (userData.referralCode) {
      return userData.referralCode;
    }
  }

  const referralCode = buildReferralCodeForUser(userId, email);

  await setDoc(
    userRef,
    {
      referralCode,
      referralEarnings: 0,
      referralClicks: 0,
      referralCredits: 0,
      referralCreditsUsed: 0,
      updatedAt: Timestamp.now(),
    },
    { merge: true }
  );

  return referralCode;
};

/**
 * Track a click on a referral link
 * If referrerUserId is not provided, it will be looked up from the referral code
 */
export const trackReferralClick = async (
  referralCode: string,
  referrerUserId?: string,
  userAgent?: string,
  ipAddress?: string
): Promise<void> => {
  try {
    let actualReferrerUserId = referrerUserId;
    
    // If referrerUserId not provided, look it up from the referral code
    if (!actualReferrerUserId) {
      const referrerQuery = query(
        collection(db, 'users'),
        where('referralCode', '==', referralCode)
      );
      const referrerSnap = await getDocs(referrerQuery);
      
      if (referrerSnap.empty) {
        console.warn(`No user found with referral code: ${referralCode}`);
        return;
      }
      
      actualReferrerUserId = referrerSnap.docs[0].id;
    }
    
    // Record the click
    await addDoc(collection(db, 'referralClicks'), {
      referralCode,
      referrerUserId: actualReferrerUserId,
      clickedAt: Timestamp.now(),
      userAgent,
      ipAddress
    });
    
    // Increment click count on user profile
    const userRef = doc(db, 'users', actualReferrerUserId);
    await updateDoc(userRef, {
      referralClicks: increment(1),
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error tracking referral click:', error);
    // Don't throw - tracking clicks shouldn't break the flow
  }
};

/**
 * Record a referral when a new user signs up
 */
export const recordReferral = async (
  referredUserId: string,
  referralCode: string
): Promise<void> => {
  try {
    // Find the referrer by code
    const referrerQuery = query(
      collection(db, 'users'),
      where('referralCode', '==', referralCode)
    );
    const referrerSnap = await getDocs(referrerQuery);
    
    if (referrerSnap.empty) {
      console.warn(`No user found with referral code: ${referralCode}`);
      return;
    }
    
    const referrerDoc = referrerSnap.docs[0];
    const referrerUserId = referrerDoc.id;
    
    // Check if referral already exists
    const existingReferralQuery = query(
      collection(db, 'referrals'),
      where('referredUserId', '==', referredUserId),
      where('referrerUserId', '==', referrerUserId)
    );
    const existingSnap = await getDocs(existingReferralQuery);
    
    if (!existingSnap.empty) {
      console.log('Referral already recorded for this user');
      return;
    }
    
    // Get referred user email
    const referredUserRef = doc(db, 'users', referredUserId);
    const referredUserSnap = await getDoc(referredUserRef);
    const referredUserEmail = referredUserSnap.data()?.email || '';
    
    // Create referral record
    await addDoc(collection(db, 'referrals'), {
      referrerUserId,
      referredUserId,
      referralCode,
      status: 'pending',
      creditAmount: REFERRAL_CREDIT_AMOUNT,
      referredUserEmail,
      createdAt: Timestamp.now()
    });
    
    // Update referred user's profile
    await updateDoc(referredUserRef, {
      referredByCode: referralCode,
      referredByUserId: referrerUserId,
      updatedAt: Timestamp.now()
    });
    
    console.log(`Referral recorded: ${referrerUserId} referred ${referredUserId}`);
  } catch (error) {
    console.error('Error recording referral:', error);
    throw error;
  }
};

/**
 * Award referral credit when a referred user becomes a paying customer
 * This should be called when a subscription is successfully created/paid
 */
export const awardReferralCredit = async (
  referredUserId: string
): Promise<void> => {
  try {
    const userRef = doc(db, 'users', referredUserId);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      console.warn(`User ${referredUserId} not found`);
      return;
    }
    
    const userData = userSnap.data();
    const referredByUserId = userData.referredByUserId;
    const referredByCode = userData.referredByCode;
    
    if (!referredByUserId || !referredByCode) {
      // User wasn't referred, nothing to do
      return;
    }
    
    // Check if credit was already awarded
    const referralQuery = query(
      collection(db, 'referrals'),
      where('referredUserId', '==', referredUserId),
      where('referrerUserId', '==', referredByUserId),
      where('status', '==', 'completed')
    );
    const completedSnap = await getDocs(referralQuery);
    
    if (!completedSnap.empty) {
      console.log('Credit already awarded for this referral');
      return;
    }
    
    // Find pending referral
    const pendingQuery = query(
      collection(db, 'referrals'),
      where('referredUserId', '==', referredUserId),
      where('referrerUserId', '==', referredByUserId),
      where('status', '==', 'pending')
    );
    const pendingSnap = await getDocs(pendingQuery);
    
    if (pendingSnap.empty) {
      console.warn(`No pending referral found for ${referredUserId}`);
      return;
    }
    
    const referralDoc = pendingSnap.docs[0];
    const referralId = referralDoc.id;
    
    // Update referral status to completed
    await updateDoc(doc(db, 'referrals', referralId), {
      status: 'completed',
      completedAt: Timestamp.now()
    });
    
    // Award credit to referrer
    const referrerRef = doc(db, 'users', referredByUserId);
    await updateDoc(referrerRef, {
      referralEarnings: increment(REFERRAL_CREDIT_AMOUNT),
      referralCredits: increment(REFERRAL_CREDIT_AMOUNT),
      updatedAt: Timestamp.now()
    });
    
    console.log(`Awarded $${REFERRAL_CREDIT_AMOUNT} credit to referrer ${referredByUserId}`);
  } catch (error) {
    console.error('Error awarding referral credit:', error);
    throw error;
  }
};

/**
 * Get referral statistics for a user
 */
export const getReferralStats = async (userId: string): Promise<{
  referralCode: string;
  referralLink: string;
  earnings: number;
  clicks: number;
  credits: number;
  creditsUsed: number;
  totalReferrals: number;
  completedReferrals: number;
}> => {
  const userRef = doc(db, 'users', userId);
  const userSnap = await getDoc(userRef);
  
  if (!userSnap.exists()) {
    throw new Error('User not found');
  }
  
  const userData = userSnap.data();
  const referralCode = userData.referralCode || '';
  
  // Get referral counts
  const referralsQuery = query(
    collection(db, 'referrals'),
    where('referrerUserId', '==', userId)
  );
  const referralsSnap = await getDocs(referralsQuery);
  
  const totalReferrals = referralsSnap.size;
  const completedReferrals = referralsSnap.docs.filter(
    doc => doc.data().status === 'completed'
  ).length;
  
  const baseUrl = window.location.origin;
  const referralLink = `${baseUrl}/signup?ref=${referralCode}`;
  
  return {
    referralCode,
    referralLink,
    earnings: userData.referralEarnings || 0,
    clicks: userData.referralClicks || 0,
    credits: userData.referralCredits || 0,
    creditsUsed: userData.referralCreditsUsed || 0,
    totalReferrals,
    completedReferrals
  };
};

/**
 * Apply referral credits to a payment amount
 * Returns the amount after applying credits and the credits used
 */
export const applyReferralCredits = async (
  userId: string,
  amount: number
): Promise<{
  finalAmount: number;
  creditsUsed: number;
  creditsRemaining: number;
}> => {
  const userRef = doc(db, 'users', userId);
  const userSnap = await getDoc(userRef);
  
  if (!userSnap.exists()) {
    return { finalAmount: amount, creditsUsed: 0, creditsRemaining: 0 };
  }
  
  const userData = userSnap.data();
  const availableCredits = userData.referralCredits || 0;
  
  if (availableCredits <= 0) {
    return { finalAmount: amount, creditsUsed: 0, creditsRemaining: 0 };
  }
  
  // Apply credits (can't go negative)
  const creditsUsed = Math.min(availableCredits, amount);
  const finalAmount = Math.max(0, amount - creditsUsed);
  const creditsRemaining = availableCredits - creditsUsed;
  
  // Update user profile
  await updateDoc(userRef, {
    referralCredits: creditsRemaining,
    referralCreditsUsed: increment(creditsUsed),
    updatedAt: Timestamp.now()
  });
  
  return {
    finalAmount,
    creditsUsed,
    creditsRemaining
  };
};

/**
 * Get referral discount for a new user (if they were referred)
 */
export const getReferralDiscount = async (userId: string): Promise<number> => {
  const userRef = doc(db, 'users', userId);
  const userSnap = await getDoc(userRef);
  
  if (!userSnap.exists()) {
    return 0;
  }
  
  const userData = userSnap.data();
  
  // Check if user was referred and hasn't used the discount yet
  if (userData.referredByCode && !userData.referralDiscountUsed) {
    return REFERRED_DISCOUNT_AMOUNT;
  }
  
  return 0;
};

/**
 * Mark referral discount as used
 */
export const markReferralDiscountUsed = async (userId: string): Promise<void> => {
  const userRef = doc(db, 'users', userId);
  await updateDoc(userRef, {
    referralDiscountUsed: true,
    updatedAt: Timestamp.now()
  });
};

/**
 * Get list of referrals with referred user details
 */
export const getReferralsWithDetails = async (referrerUserId: string): Promise<Array<{
  id: string;
  referralCode: string;
  status: 'pending' | 'completed' | 'cancelled';
  creditAmount: number;
  referredUserEmail: string;
  referredUserId: string;
  createdAt: Timestamp;
  completedAt?: Timestamp;
  referredUserName?: string;
}>> => {
  try {
    const referralsQuery = query(
      collection(db, 'referrals'),
      where('referrerUserId', '==', referrerUserId),
      orderBy('createdAt', 'desc')
    );
    const referralsSnap = await getDocs(referralsQuery);
    
    const referrals = [];
    
    for (const referralDoc of referralsSnap.docs) {
      const referralData = referralDoc.data();
      const referredUserId = referralData.referredUserId;
      
      // Get referred user details
      let referredUserName = '';
      try {
        const referredUserRef = doc(db, 'users', referredUserId);
        const referredUserSnap = await getDoc(referredUserRef);
        if (referredUserSnap.exists()) {
          const userData = referredUserSnap.data();
          referredUserName = userData.firstName && userData.lastName
            ? `${userData.firstName} ${userData.lastName}`
            : userData.displayName || userData.email || '';
        }
      } catch (error) {
        console.error('Error fetching referred user details:', error);
      }
      
      referrals.push({
        id: referralDoc.id,
        referralCode: referralData.referralCode,
        status: referralData.status,
        creditAmount: referralData.creditAmount || REFERRAL_CREDIT_AMOUNT,
        referredUserEmail: referralData.referredUserEmail || '',
        referredUserId,
        createdAt: referralData.createdAt,
        completedAt: referralData.completedAt,
        referredUserName
      });
    }
    
    return referrals;
  } catch (error) {
    console.error('Error getting referrals with details:', error);
    throw error;
  }
};

export const referralService = {
  generateReferralCode,
  getOrCreateReferralCode,
  trackReferralClick,
  recordReferral,
  awardReferralCredit,
  getReferralStats,
  applyReferralCredits,
  getReferralDiscount,
  markReferralDiscountUsed,
  getReferralsWithDetails
};

